'use client';

import { ButtonV1, SurprisedEmojiAnimation } from '@northladder/design-system';
import { useTranslations } from '@northladder/i18n';

import { DisplayDeviceName } from '../DisplayDeviceName';

interface IAssessmentCompleteFailedProps {
  evaluationId: string;
  hasMissedFeatures: boolean;
  onRestart: () => void;
  onRetry: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This is rendered when the user has failed to submit the assessment.
 */
export function AssessmentCompleteFailed({
  evaluationId,
  hasMissedFeatures,
  onRestart,
  onRetry,
}: IAssessmentCompleteFailedProps) {
  const t = useTranslations('FunctionalAssessmentComplete.fallbackScreen');

  const instructionLine1Text = hasMissedFeatures
    ? t('instructionLine1NoAssessments')
    : t('instructionLine1');
  const instructionLine2Text = hasMissedFeatures
    ? t('instructionLine2NoAssessments')
    : t('instructionLine2');

  return (
    <div className="flex flex-col items-center justify-center gap-10 pt-8">
      <div className="flex flex-col items-center justify-center gap-2 px-4">
        <h1 className="text-center text-2xl font-bold leading-8">
          {t('introText')}
        </h1>
        <div
          className="
            text-center text-sm font-bold leading-4 text-red-900 opacity-95
            dark:text-red-500
          "
        >
          {t('subTitle')}
          <DisplayDeviceName evaluationId={evaluationId} />
        </div>
      </div>
      <SurprisedEmojiAnimation className="w-60 mix-blend-luminosity" />
      <div
        className="
          flex max-w-xs flex-col items-center justify-center gap-2 pb-10
          text-center text-sm font-normal leading-5
        "
      >
        <p className="flex flex-col text-red-800 dark:text-red-500">
          {instructionLine1Text}
        </p>
        <hr className="my-2 w-full border-gray-400/10" />
        <span className="text-primary-600 dark:text-primary-300 text-xs">
          {instructionLine2Text}
        </span>

        {hasMissedFeatures ? null : (
          <>
            <div className="my-4 flex w-full justify-end">
              <ButtonV1
                className="mx-auto w-auto px-16"
                isEnabled
                isLoading={false}
                label={t('retryCTA')}
                onClick={onRetry}
                type="button"
              />
            </div>

            <div className="text-sm font-light text-gray-500 dark:text-gray-300">
              {t('beginAfresh')}

              <button
                className="form-link-action ms-2"
                onClick={onRestart}
                type="button"
              >
                {t('restartCTA')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
